// Full-screen image pager with optional before/after compare and a caption.
// The image fits the window by default (object-contain) and can be zoomed
// (scroll / double-click / buttons) and panned (drag) to inspect render detail.
import { useCallbackRef } from "../lib/useCallbackRef";
import { useEffect, useRef, useState } from "react";
import { useAssetUrl } from "./AssetImage";

const MAX_ZOOM = 6;
const MIN_ZOOM = 1;

export interface LightboxSlide {
  /** Content-addressed blob key (resolved to a presigned URL). */
  s3Key?: string;
  /** Direct presigned URL (job-fallback renders) — used as-is. */
  url?: string;
  caption?: string;
  /** Optional "before" image (source photo) to compare against. */
  beforeS3Key?: string;
  /** Optional action node (e.g. a save-to-ideabook button). */
  action?: React.ReactNode;
}

export function Lightbox({
  slides,
  index,
  onClose,
  onIndexChange,
}: {
  slides: LightboxSlide[];
  index: number;
  onClose: () => void;
  onIndexChange: (i: number) => void;
}) {
  const [compare, setCompare] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const slide = slides[index];
  const resolvedUrl = useAssetUrl(slide?.url ? null : slide?.s3Key);
  const url = slide?.url ?? resolvedUrl;
  const beforeUrl = useAssetUrl(compare ? slide?.beforeS3Key : null);

  const resetZoom = useCallbackRef(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  });
  const prev = useCallbackRef(() => {
    resetZoom();
    onIndexChange((index - 1 + slides.length) % slides.length);
  });
  const next = useCallbackRef(() => {
    resetZoom();
    onIndexChange((index + 1) % slides.length);
  });
  const zoomBy = useCallbackRef((factor: number) => {
    setZoom((z) => {
      const nz = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z * factor));
      if (nz === 1) setPan({ x: 0, y: 0 });
      return nz;
    });
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "+" || e.key === "=") zoomBy(1.4);
      if (e.key === "-" || e.key === "_") zoomBy(1 / 1.4);
      if (e.key === "0") resetZoom();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next, zoomBy, resetZoom]);

  // Reset compare + zoom whenever the visible image changes.
  useEffect(() => {
    setCompare(false);
    resetZoom();
  }, [index, resetZoom]);

  if (!slide) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/90 backdrop-blur-sm" onClick={onClose}>
      <div className="flex items-center justify-between p-4 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm opacity-80">
          {index + 1} / {slides.length}
          {slide.caption && <span className="ml-3 opacity-100">{slide.caption}</span>}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-full bg-white/10">
            <button className="rounded-full px-3 py-1.5 text-base leading-none hover:bg-white/20" onClick={() => zoomBy(1 / 1.4)} aria-label="Zoom out">−</button>
            <button className="min-w-[3.25rem] px-1 py-1.5 text-xs hover:bg-white/20" onClick={resetZoom} title="Reset zoom">{Math.round(zoom * 100)}%</button>
            <button className="rounded-full px-3 py-1.5 text-base leading-none hover:bg-white/20" onClick={() => zoomBy(1.4)} aria-label="Zoom in">+</button>
          </div>
          {slide.beforeS3Key && (
            <button
              className="rounded-full bg-white/15 px-3 py-1.5 text-sm hover:bg-white/25"
              onClick={() => setCompare((c) => !c)}
            >
              {compare ? "Show after" : "Compare before"}
            </button>
          )}
          {slide.action}
          <button className="rounded-full bg-white/15 px-3 py-1.5 text-sm hover:bg-white/25" onClick={onClose}>
            Close ✕
          </button>
        </div>
      </div>

      <div
        className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden p-4"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => {
          e.preventDefault();
          zoomBy(e.deltaY < 0 ? 1.15 : 1 / 1.15);
        }}
        onDoubleClick={() => (zoom === 1 ? setZoom(2.5) : resetZoom())}
        onPointerDown={(e) => {
          if (zoom === 1) return;
          (e.target as Element).setPointerCapture?.(e.pointerId);
          dragRef.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
        }}
        onPointerMove={(e) => {
          const d = dragRef.current;
          if (!d) return;
          setPan({ x: d.px + (e.clientX - d.x), y: d.py + (e.clientY - d.y) });
        }}
        onPointerUp={() => {
          dragRef.current = null;
        }}
        style={{ cursor: zoom > 1 ? (dragRef.current ? "grabbing" : "grab") : "zoom-in" }}
      >
        {slides.length > 1 && (
          <button
            className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/15 p-3 text-white hover:bg-white/25"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
          >
            ‹
          </button>
        )}
        {(compare && beforeUrl ? beforeUrl : url) && (
          <img
            src={(compare && beforeUrl ? beforeUrl : url) as string}
            alt={slide.caption ?? ""}
            draggable={false}
            className="max-h-full max-w-full select-none rounded-lg object-contain shadow-2xl"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transition: dragRef.current ? "none" : "transform 0.12s ease-out",
            }}
          />
        )}
        {compare && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-sm text-white">
            Before
          </div>
        )}
        {slides.length > 1 && (
          <button
            className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/15 p-3 text-white hover:bg-white/25"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
          >
            ›
          </button>
        )}
      </div>
    </div>
  );
}
